import { useEffect, useState, useCallback } from 'react';
import { Card, Typography, Table, Button, Tag, Space, Modal, Form, Input, Switch, Select, InputNumber, message, Popconfirm, Alert } from 'antd';
import { KeyOutlined, PlusOutlined, CopyOutlined } from '@ant-design/icons';
import { useApi } from '../../hooks/use_api';
import { useAuth } from '../../context/AuthContext';

const { Title, Text, Paragraph } = Typography;

/** Agent tokens: credentials a person mints for their own AI agent (Claude,
 * ChatGPT, a script) to use this node through the API or the MCP server.
 * Read-only unless allowed to write, optionally bound to one group, always
 * expiring, revocable, and every write is audited. The rules live in the
 * backend (services/agent_tokens.py). */
function AgentTokens() {
    const api = useApi();
    const { userPersonEntity } = useAuth();
    const [tokens, setTokens] = useState([]);
    const [loading, setLoading] = useState(true);
    const [groups, setGroups] = useState([]);
    const [creating, setCreating] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [secret, setSecret] = useState(null);
    const [busyId, setBusyId] = useState(null);
    const [form] = Form.useForm();

    const load = useCallback(() => {
        setLoading(true);
        return api.get('/api/agent-tokens')
            .then(setTokens)
            .catch(() => message.error('Could not load agent tokens'))
            .finally(() => setLoading(false));
    }, [api]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        if (!userPersonEntity?.id || !modalOpen) return;
        api.get(`/api/entities/${userPersonEntity.id}/connected`)
            .then(rows => setGroups(rows
                .filter(r => r.connected_status === true && ['context', 'relationship'].includes(r.entity.entity_type) && !r.entity.attributes?.is_dm)
                .map(r => ({ value: r.entity.id, label: r.entity.name }))))
            .catch(() => setGroups([]));
    }, [api, userPersonEntity?.id, modalOpen]);

    const handleCreate = values => {
        setCreating(true);
        api.post('/api/agent-tokens', {
            name: values.name.trim(),
            can_write: !!values.can_write,
            context_id: values.context_id ?? null,
            expires_in_days: values.expires_in_days,
        })
            .then(created => {
                setSecret(created.token);
                setModalOpen(false);
                form.resetFields();
                return load();
            })
            .catch(err => message.error(err.message || 'Could not create the token'))
            .finally(() => setCreating(false));
    };

    const handleRevoke = id => {
        setBusyId(id);
        api.del(`/api/agent-tokens/${id}`)
            .then(() => { message.success('Token revoked'); return load(); })
            .catch(err => message.error(err.message || 'Could not revoke the token'))
            .finally(() => setBusyId(null));
    };

    const columns = [
        {
            title: 'Name',
            key: 'name',
            render: (_, row) => (
                <div>
                    <Text strong>{row.name}</Text>
                    <div><Text type="secondary" style={{ fontSize: 12 }}>{row.prefix}…</Text></div>
                </div>
            ),
        },
        {
            title: 'Can',
            key: 'scope',
            render: (_, row) => (
                <Space size={4} wrap>
                    <Tag>{row.can_write ? 'read and write' : 'read only'}</Tag>
                    {row.context_id && <Tag>one group</Tag>}
                </Space>
            ),
        },
        {
            title: 'Expires',
            dataIndex: 'expires_at',
            key: 'expires_at',
            render: ts => new Date(ts).toLocaleDateString(),
        },
        {
            title: 'Last used',
            dataIndex: 'last_used_at',
            key: 'last_used_at',
            render: ts => ts ? new Date(ts).toLocaleString() : 'never',
        },
        {
            title: '',
            key: 'actions',
            render: (_, row) => row.active ? (
                <Popconfirm title="Revoke this token?" description="The agent using it stops working at once." onConfirm={() => handleRevoke(row.id)} okText="Revoke" okButtonProps={{ danger: true }}>
                    <Button size="small" danger loading={busyId === row.id}>Revoke</Button>
                </Popconfirm>
            ) : <Tag>{row.revoked_at ? 'revoked' : 'expired'}</Tag>,
        },
    ];

    return (
        <Card style={{ marginTop: 16 }}>
            <Space align="center" style={{ width: '100%', justifyContent: 'space-between', marginBottom: 8 }}>
                <Title level={5} style={{ margin: 0 }}><KeyOutlined /> Agent access</Title>
                <Button icon={<PlusOutlined />} onClick={() => setModalOpen(true)}>New token</Button>
            </Space>
            <Paragraph type="secondary" style={{ marginBottom: 12 }}>
                Vellby has no built-in assistant: use your own AI agent through this node's MCP server or API.
                Give it a token from here rather than your login. A token can never change connections, invites,
                your account or node settings, and everything it writes is logged.
            </Paragraph>
            <Table rowKey="id" loading={loading} columns={columns} dataSource={tokens} pagination={false} size="small" />

            <Modal
                title="New agent token"
                open={modalOpen}
                onCancel={() => setModalOpen(false)}
                onOk={() => form.submit()}
                okText="Create"
                okButtonProps={{ loading: creating }}
                destroyOnClose
            >
                <Form form={form} layout="vertical" onFinish={handleCreate} initialValues={{ can_write: false, expires_in_days: 30 }}>
                    <Form.Item name="name" label="Name" rules={[{ required: true, whitespace: true, message: 'Name the agent' }]}>
                        <Input placeholder="e.g. Claude on my laptop" maxLength={80} />
                    </Form.Item>
                    <Form.Item name="can_write" label="May post messages and edit entities" valuePropName="checked">
                        <Switch />
                    </Form.Item>
                    <Form.Item name="context_id" label="Limit to one group (optional)">
                        <Select allowClear placeholder="Everything you can see" options={groups} showSearch optionFilterProp="label" />
                    </Form.Item>
                    <Form.Item name="expires_in_days" label="Expires after (days)" rules={[{ required: true }]}>
                        <InputNumber min={1} max={90} style={{ width: '100%' }} />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title="Copy your token now"
                open={!!secret}
                onCancel={() => setSecret(null)}
                footer={<Button type="primary" onClick={() => setSecret(null)}>Done</Button>}
            >
                <Alert type="warning" showIcon style={{ marginBottom: 12 }} message="This is the only time the token is shown." />
                <Paragraph copyable={{ text: secret || '', icon: <CopyOutlined /> }} code style={{ wordBreak: 'break-all' }}>
                    {secret}
                </Paragraph>
                <Text type="secondary">Give it to your agent as a bearer token, or run <Text code>vellby auth login --token …</Text>.</Text>
            </Modal>
        </Card>
    );
}

export default AgentTokens;
